/* eslint-disable no-unused-vars */
import { maitreOuvrageIdentifie } from './MaitreOuvrageBloc';
import { motifFtpeActif } from '../../components/FtpeMasqueToggle';
import { echeancierMasque } from '../../components/EcheancierMasqueToggle';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { showFormErrorFeedback } from '../../lib/formFeedback';
import { computeTtcFromHtAndTaxes, normalizeTaxesSource } from '../../lib/computeTtc';
import { API } from './constants';

export function useDevisDownload(deps) {
  const {
    contratParent, creditCostPdf, currentEcheancierPlan, formData, getActivitesSelectionneesFull, getMontantsFinals,
    getTaxesFinals, handleSouscrireFromProposition, isAdminOrSuperAdmin, isFtpeMember, isSuperAdmin, location,
    membreData, modeCalcul, operationNominative, resultat, resumeParentDevisId, selectedActivites2nd,
    selectedActivitesGO, selectedActivitesPIB, selectedActivitesTP, setLoadingDevis, setPropositionConversion, setResumeParentDevisId,
    setWalletBalance, validitePersonnalisee, walletBalance, repriseSimulateur,
  } = deps;

  const handleDownloadDevisSimulation = async () => {
    if (!resultat) {
      toast.error('Veuillez d\'abord effectuer une simulation');
      return;
    }

    const token = getToken();
    if (!token) {
      toast.error('Connexion requise pour télécharger la proposition');
      return;
    }

    if (modeCalcul === 'nominative' && !maitreOuvrageIdentifie(formData.maitre_ouvrage)) {
      toast.error("Identification du maître d'ouvrage obligatoire", {
        description: "Complétez le bloc « Identification du maître d'ouvrage » sous les détails de l'opération.",
        duration: 7000,
      });
      return;
    }

    const montantGlobal = Number(getMontantsFinals()?.montant_ttc || resultat.montant_ttc || 0);
    if (!(montantGlobal > 0)) {
      toast.error('Montant global non calculé', {
        description: 'Renseignez le CA et les activités puis lancez le calcul avant de générer la proposition.',
        duration: 7000,
      });
      return;
    }

    // Les admins et super_admins n'ont pas besoin de crédits
    if (!isAdminOrSuperAdmin) {
      // Vérifier le solde si le wallet existe
      if (walletBalance !== null && walletBalance < creditCostPdf) {
        toast.error(`Solde insuffisant. Vous avez ${walletBalance} crédits, il vous en faut ${creditCostPdf}.`, {
          description: 'Rechargez votre wallet pour continuer.',
          duration: 5000
        });
        return;
      }

      // Confirmation du coût en crédits
      if (walletBalance !== null) {
        const confirmed = window.confirm(
          `Cette action consommera ${creditCostPdf} crédits.\nSolde actuel: ${walletBalance} crédits.\n\nConfirmer la génération du PDF ?`
        );
        if (!confirmed) return;
      }
    }

    setLoadingDevis(true);
    try {
      // Récupérer les activités complètes avec définitions et exclusions
      const activitesFullDetails = getActivitesSelectionneesFull();
      
      // Préparer les données de la simulation avec les détails complets
      const allActivites = [
        ...activitesFullDetails.gros_oeuvre.map(a => ({ ...a, famille: 'Gros Œuvre' })),
        ...activitesFullDetails.second_oeuvre.map(a => ({ ...a, famille: 'Second Œuvre' })),
        ...activitesFullDetails.travaux_publics.map(a => ({ ...a, famille: 'Travaux Publics' })),
        ...activitesFullDetails.pib.map(a => ({ ...a, famille: 'PIB' }))
      ];

      // Récupérer les montants et taxes personnalisés
      const montantsFinals = getMontantsFinals();
      const taxesFinals = getTaxesFinals();

      const devisData = {
        raison_sociale: contratParent?.raison_sociale || formData.raison_sociale || membreData?.raison_sociale || 'SIMULATION',
        siret: contratParent?.siret || formData.siret || membreData?.siret || '',
        chiffre_affaires: montantsFinals?.ca || parseFloat(formData.ca_ht) || 0,
        activites: allActivites,
        activites_details: activitesFullDetails,
        // Montants (avec personnalisation Super Admin si activée)
        montant_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
        montant_ttc: montantsFinals?.montant_ttc || resultat.montant_ttc || 0,
        detail_calcul: resultat.detail_calcul || {},
        // Taxes (avec personnalisation Super Admin si activée)
        taxes: taxesFinals?.is_personnalise ? { detail: taxesFinals.detail, is_personnalise: true } : (resultat.taxes || {}),
        mode_calcul: modeCalcul,
        territoire: contratParent?.territoire || membreData?.territoire || 'Guadeloupe',
        // Type de souscription et période nominative
        type_souscription: modeCalcul === 'nominative' ? 'nominative' : 'annuel',
        nombre_jours: modeCalcul === 'nominative' ? parseInt(formData.nombre_jours) || null : null,
        // Opération nominative (si mode nominative)
        operation_nominative: modeCalcul === 'nominative' ? {
          type_operation: operationNominative.type_operation || null,
          description_operation: operationNominative.description_operation || null,
          territoire_intervention: operationNominative.territoire_intervention || null,
          adresse_operation: operationNominative.adresse_operation || null,
          code_postal_operation: operationNominative.code_postal_operation || null,
          ville_operation: operationNominative.ville_operation || null,
          pays_operation: operationNominative.pays_operation || 'France'
        } : null,
        // Maître d'ouvrage (toujours inclus si renseigné)
        maitre_ouvrage: formData.maitre_ouvrage || null,
        // Bénéficiaire / Membre — remplit dynamiquement le bloc du PDF Proposition ESS
        // lorsque la simulation est lancée depuis le tableau des membres (modes
        // ?nouveau=true, ?prefill=true ou ?resume=true). Le backend complète les
        // champs manquants via lookup MongoDB sur membre_id.
        ...(membreData && {
          membre_id: membreData.membre_id || null,
          nom_gerant: membreData.nom || '',
          prenom_gerant: membreData.prenom || '',
          email: membreData.email || '',
          telephone: membreData.telephone || '',
          adresse: membreData.adresse || '',
          code_postal: membreData.code_postal || '',
          ville: membreData.ville || '',
          statut_juridique: membreData.statut_juridique || membreData.forme_juridique || ''
        }),
        // Montants personnalisés (Super Admin uniquement)
        ...(montantsFinals?.is_personnalise && {
          montants_personnalises: {
            ca_personnalise: montantsFinals.ca,
            montant_ht_personnalise: montantsFinals.montant_ht,
            montant_ttc_personnalise: montantsFinals.montant_ttc,
            remise_pourcentage: montantsFinals.remise,
            remise_euros: montantsFinals.remise_euros || 0
          }
        }),
        // Taxes personnalisées (Super Admin uniquement)
        ...(taxesFinals?.is_personnalise && {
          taxes_personnalisees: taxesFinals.detail
        }),
        // Période de validité personnalisée (Super Admin uniquement)
        ...(isSuperAdmin && validitePersonnalisee.enabled && {
          periode_personnalisee: {
            date_debut: validitePersonnalisee.date_debut || null,
            date_fin: validitePersonnalisee.date_fin || null,
            validite_jours: validitePersonnalisee.validite_jours || 365
          }
        }),
        // Flag FTPE pour conditionner les blocs dans le PDF
        is_ftpe_member: isFtpeMember,
        ftpe_masque_motif: motifFtpeActif(),
        masquer_echeancier: isSuperAdmin && echeancierMasque(),
        avenant_parent_id: contratParent ? (contratParent.id || contratParent.numero_police || null) : null,
        avenant_prorata: contratParent && resultat.prorata ? resultat.prorata : null,
        // V39.54 — Proposition issue d'une réouverture de souscription depuis
        // l'espace membre → badge « Reprise simulateur » dans Mes Documents.
        reprise_simulateur: !!repriseSimulateur,
        renouvellement_de: new URLSearchParams(window.location.search).get('renouveler') || null,
        validite_devis_jours: (isSuperAdmin && validitePersonnalisee.enabled && validitePersonnalisee.validite_jours)
          ? (parseInt(validitePersonnalisee.validite_jours, 10) || 30)
          : (modeCalcul === 'nominative' && formData.nombre_jours ? (parseInt(formData.nombre_jours, 10) || 30) : 30),
        // V36.73 — Échéancier personnalisé Super Admin (acompte, nb échéances,
        // offsets, seuil, forçage). Le PDF backend l'utilise via
        // `souscription_data.echelonnement_override` pour produire EXACTEMENT
        // le même tableau d'échéances que l'écran.
        ...(isSuperAdmin && currentEcheancierPlan && currentEcheancierPlan.is_custom && {
          echelonnement_override: {
            acompte_pct: currentEcheancierPlan.acompte_pct,
            nb_echeances: currentEcheancierPlan.nb_echeances,
            offsets_days: currentEcheancierPlan.offsets_days,
            acompte_date: currentEcheancierPlan.acompte_date,
            dates_iso: currentEcheancierPlan.dates_iso,
            montants_eur: currentEcheancierPlan.montants_eur,
            force_eligible: currentEcheancierPlan.force_eligible,
            seuil_eur: currentEcheancierPlan.seuil_eur,
          },
        }),
      };

      // Générer la proposition ESS
      const response = await axios.post(
        `${API}/souscriptions/simulateur/generate-devis`,
        devisData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Télécharger le PDF via axios (plus fiable que le lien direct)
        const devisId = response.data.devis_id;
        const downloadUrl = `${API}/souscriptions/simulateur/download-devis/${devisId}`;
        
        try {
          const pdfResponse = await axios.get(downloadUrl, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          
          // Créer un blob URL et télécharger
          const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `Proposition_ESS_${devisId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (downloadError) {
          toast.error('Erreur lors du téléchargement du PDF. Veuillez réessayer.');
          setLoadingDevis(false);
          return;
        }

        // ============================================================
        // BANNIÈRE DE CONVERSION — Proposition → Souscription
        // ============================================================
        // Calculer la durée de validité dynamiquement :
        //   1. Personnalisée Super Admin (si activée)
        //   2. Période nominative (nombre_jours)
        //   3. Défaut : 30 jours (validité standard d'une proposition commerciale)
        let validiteJours = 30;
        if (isSuperAdmin && validitePersonnalisee.enabled && validitePersonnalisee.validite_jours) {
          validiteJours = parseInt(validitePersonnalisee.validite_jours) || 30;
        } else if (modeCalcul === 'nominative' && formData.nombre_jours) {
          validiteJours = parseInt(formData.nombre_jours) || 30;
        }
        const dateValiditeObj = new Date();
        dateValiditeObj.setDate(dateValiditeObj.getDate() + validiteJours);
        const dateValidite = dateValiditeObj.toLocaleDateString('fr-FR', {
          day: '2-digit', month: 'long', year: 'numeric',
        });

        // Snapshot complet pour pré-remplir le workflow de souscription sans
        // ressaisie. Stocké en sessionStorage pour survivre à un rafraîchissement
        // de page lors de la transition vers le workflow.
        const snapshot = {
          devis_id: devisId,
          validite_jours: validiteJours,
          date_validite_iso: dateValiditeObj.toISOString(),
          avenant: contratParent ? {
            parent_id: contratParent.id || null,
            numero_police: contratParent.numero_police || null,
            date_fin: contratParent.date_fin || contratParent.periode_adhesion_fin || null,
            prorata: resultat.prorata || null,
            prorata_config: (() => { try { return JSON.parse(sessionStorage.getItem('prorata_avenant_config') || 'null'); } catch { return null; } })(),
          } : null,
          tarification: {
            ...(resultat._brut && { _brut: resultat._brut, prorata: resultat.prorata }),
            montant_total_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
            montant_ht: montantsFinals?.montant_ht || resultat.montant_ht || 0,
            montant_ttc: montantsFinals?.montant_ttc || resultat.montant_ttc || 0,
            taxes: taxesFinals?.is_personnalise
              ? { detail: taxesFinals.detail, is_personnalise: true }
              : (resultat.taxes || {}),
            detail_calcul: resultat.detail_calcul || {},
          },
          ca_ht: montantsFinals?.ca || parseFloat(formData.ca_ht) || 0,
          type_souscription: modeCalcul === 'nominative' ? 'nominative' : 'annuel',
          nombre_jours: modeCalcul === 'nominative' ? parseInt(formData.nombre_jours) || null : null,
          activites_selectionnees: {
            gros_oeuvre: selectedActivitesGO,
            second_oeuvre: selectedActivites2nd,
            travaux_publics: selectedActivitesTP,
            pib: selectedActivitesPIB,
          },
          activites_details: activitesFullDetails,
          membre_prefill: membreData || null,
          montants_personnalises: montantsFinals?.is_personnalise ? {
            ca_personnalise: montantsFinals.ca,
            montant_ht_personnalise: montantsFinals.montant_ht,
            montant_ttc_personnalise: montantsFinals.montant_ttc,
            remise_pourcentage: montantsFinals.remise,
            remise_euros: montantsFinals.remise_euros || 0,
          } : null,
          taxes_personnalisees: taxesFinals?.is_personnalise ? taxesFinals.detail : null,
          is_ftpe_member: isFtpeMember,
          ftpe_masque_motif: motifFtpeActif(),
          operation_nominative: modeCalcul === 'nominative' ? {
            type_operation: operationNominative.type_operation || null,
            description_operation: operationNominative.description_operation || null,
            territoire_intervention: operationNominative.territoire_intervention || null,
            adresse_operation: operationNominative.adresse_operation || null,
            code_postal_operation: operationNominative.code_postal_operation || null,
            ville_operation: operationNominative.ville_operation || null,
            pays_operation: operationNominative.pays_operation || 'France',
          } : null,
          maitre_ouvrage: formData.maitre_ouvrage || null,
          periode_personnalisee: (isSuperAdmin && validitePersonnalisee.enabled) ? {
            date_debut: validitePersonnalisee.date_debut || null,
            date_fin: validitePersonnalisee.date_fin || null,
            validite_jours: validitePersonnalisee.validite_jours || 365,
          } : null,
          // V36.74 — Échéancier personnalisé Super Admin persisté sur la
          // proposition. Réutilisé par le workflow Paiement (SepaMandateBlock)
          // pour pré-remplir le mandat SEPA GoCardless avec exactement le même
          // plan que celui affiché sur le PDF Proposition ESS.
          echelonnement_override: (
            isSuperAdmin && currentEcheancierPlan && currentEcheancierPlan.is_custom
              ? {
                  acompte_pct: currentEcheancierPlan.acompte_pct,
                  nb_echeances: currentEcheancierPlan.nb_echeances,
                  offsets_days: currentEcheancierPlan.offsets_days,
            acompte_date: currentEcheancierPlan.acompte_date,
            dates_iso: currentEcheancierPlan.dates_iso,
            montants_eur: currentEcheancierPlan.montants_eur,
                  force_eligible: currentEcheancierPlan.force_eligible,
                  seuil_eur: currentEcheancierPlan.seuil_eur,
                }
              : null
          ),
          source: 'batico_proposition_ess',
          generated_at: new Date().toISOString(),
        };
        try {
          sessionStorage.setItem(
            `batico_proposition_${devisId}`,
            JSON.stringify(snapshot)
          );
          // Pointeur vers la dernière proposition pour récupération facile
          // dans le workflow cible.
          sessionStorage.setItem('batico_proposition_latest', devisId);
        } catch (_) {
          // sessionStorage indisponible (mode privé strict, quota dépassé) :
          // la conversion fonctionnera toujours via location.state.
        }

        setPropositionConversion({
          devisId,
          validiteJours,
          dateValidite,
          dateValiditeISO: dateValiditeObj.toISOString(),
          membreEmail: membreData?.email || null,
          membreName: (
            membreData?.raison_sociale
            || (membreData?.prenom && membreData?.nom
              ? `${membreData.prenom} ${membreData.nom}`
              : null)
          ),
        });

        // Persistance backend + email Brevo (idempotent) — fire-and-forget :
        // l'utilisateur n'est pas bloqué par cet appel, et un échec réseau ne
        // doit pas masquer le succès du téléchargement PDF. La bannière
        // sessionStorage couvre déjà la même session ; cette persistance ouvre
        // la conversion différée (email avec lien signé valable validite_jours).
        axios.post(
          `${API}/batico/proposition/persist-and-email`,
          {
            devis_id: devisId,
            validite_jours: validiteJours,
            snapshot,
            membre_id: membreData?.membre_id || membreData?.id || null,
            membre_email: membreData?.email || null,
            membre_name: (
              membreData?.raison_sociale
              || (membreData?.prenom && membreData?.nom
                ? `${membreData.prenom} ${membreData.nom}`
                : null)
            ),
            // Versioning : si on est dans une session "resume", on rattache
            // la nouvelle proposition à la lignée parente (v_n+1).
            parent_devis_id: resumeParentDevisId || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((res) => {
          // Si une nouvelle version a été créée (>=2), on bascule le pointeur
          // resumeParentDevisId sur la lignée pour que les ajustements
          // ultérieurs continuent dans la même lignée (v3, v4...).
          const newVersion = res?.data?.version;
          const parentFromServer = res?.data?.parent_devis_id;
          if (newVersion && newVersion > 1 && parentFromServer) {
            setResumeParentDevisId(parentFromServer);
            toast.info(`Version v${newVersion} enregistrée`, {
              description: 'Cette proposition s\'ajoute à l\'historique de la lignée.',
              duration: 4500,
            });
          }
        }).catch(() => {
          // Pas de toast d'erreur : la bannière sessionStorage suffit pour la
          // session courante. Logging silencieux suffisant.
        });

        // Toast immédiat avec action "Souscrire en 1 clic"
        toast.success('Proposition ESS téléchargée !', {
          description: `Valable jusqu'au ${dateValidite} — Souscrire en 1 clic ?`,
          duration: 8000,
          action: {
            label: 'Souscrire',
            onClick: () => handleSouscrireFromProposition(),
          },
        });
        
        // Consommer les crédits après génération réussie
        if (walletBalance !== null) {
          try {
            const consumeResponse = await axios.post(
              `${API}/wallets/consume`,
              {
                action_type: 'generation_pdf',
                reference_id: devisId,
                reference_type: 'batico_devis',
                description: 'Génération Proposition ESS BATICO'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Mettre à jour le solde local
            if (consumeResponse.data.balance_after !== undefined) {
              setWalletBalance(consumeResponse.data.balance_after);
            }
          } catch (creditError) {
            // PDF généré OK même si débit crédits échoue
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération de la proposition');
    } finally {
      setLoadingDevis(false);
    }
  };

  return {
    handleDownloadDevisSimulation,
  };
}

export default useDevisDownload;
